import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Typography,
  Table,
  Tag,
  Progress,
  Tabs,
  List,
  Avatar,
  Timeline,
  DatePicker,
  Select,
  Input,
  Modal,
  message,
  Badge,
  Divider
} from 'antd';
import {
  DollarOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  TrophyOutlined,
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  SendOutlined,
  FileTextOutlined,
  CalendarOutlined,
  BarChartOutlined,
  TeamOutlined,
  CopyOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Popconfirm } from 'antd';

// Import the new components
import InvoiceBuilder from '../components/InvoiceBuilder';
import QuotationBuilder from '../components/QuotationBuilder';
import { SalesOrderList } from '../components/SalesOrder';
import SalesOrderBuilder from '../components/SalesOrderBuilder';
import SalesTrendChart from '../components/Analytics/SalesTrendChart';
import RevenueBreakdownChart from '../components/Analytics/RevenueBreakdownChart';
import { useIsMobile, getMobileTableProps, getMobileButtonProps } from '../../../utils/responsiveUtils';
import apiService from '../../../services/apiService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;
const { TabPane } = Tabs;

interface SalesData {
  totalRevenue: number;
  totalOrders: number;
  avgOrderValue: number;
  conversionRate: number;
  topProducts: Array<{
    id: string;
    name: string;
    sales: number;
    revenue: number;
  }>;
  recentOrders: Array<{
    id: string;
    customer: string;
    amount: number;
    status: string;
    date: string;
  }>;
  invoices: Array<{
    id: string;
    invoiceNumber: string;
    customer: string;
    amount: number;
    status: 'draft' | 'sent' | 'paid' | 'overdue';
    dueDate: string;
  }>;
  quotations: Array<{
    id: string;
    quoteNumber: string;
    customer: string;
    amount: number;
    status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired';
    validUntil: string;
    createdDate: string;
  }>;
}

const SalesOverview: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  
  const [salesData, setSalesData] = useState<SalesData>({
    totalRevenue: 0,
    totalOrders: 0,
    avgOrderValue: 0,
    conversionRate: 0,
    topProducts: [],
    recentOrders: [],
    invoices: [],
    quotations: []
  });
  const [loading, setLoading] = useState(false);
  // Quotations list states (server pagination)
  const [quotationLoading, setQuotationLoading] = useState(false);
  const [quotationPage, setQuotationPage] = useState(1);
  const [quotationPageSize, setQuotationPageSize] = useState(10);
  const [quotationTotal, setQuotationTotal] = useState(0);
  const [quotationStatus, setQuotationStatus] = useState<string | undefined>(undefined);
  const [quotationSearch, setQuotationSearch] = useState('');
  const [quotationDateRange, setQuotationDateRange] = useState<[string, string] | null>(null);
  const [quotationRows, setQuotationRows] = useState<any[]>([]);
  
  // Set active tab based on current route
  const getActiveTabFromRoute = () => {
    const path = location.pathname;
    if (path.includes('/sales/orders')) return 'orders';
    if (path.includes('/sales/quotations')) return 'quotations';
    if (path.includes('/sales/invoices')) return 'invoices';
    return 'overview';
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTabFromRoute());
  const [invoiceModalVisible, setInvoiceModalVisible] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState<string | null>(null);
  const [orderBuilderVisible, setOrderBuilderVisible] = useState(false);
  const [quotationModalVisible, setQuotationModalVisible] = useState(false);
  const [editingQuotationId, setEditingQuotationId] = useState<string | null>(null);

  // Open create-invoice modal helper: ensure clean state (no editId)
  const openCreateInvoice = () => {
    try { localStorage.removeItem('invoice.editId'); } catch {}
    setEditingInvoiceId(null);
    setInvoiceModalVisible(true);
  };

  const openCreateQuotation = () => {
    try { localStorage.removeItem('quotation.editId'); } catch {}
    setEditingQuotationId(null);
    setQuotationModalVisible(true);
  };

  // Update active tab when route changes
  useEffect(() => {
    setActiveTab(getActiveTabFromRoute());
  }, [location.pathname]);

  useEffect(() => {
    const loadOverview = async () => {
      try {
        // 先以空值初始化，待各區塊各自載入
        setSalesData(prev => ({
          ...prev,
          topProducts: [],
          recentOrders: [],
          invoices: [],
          quotations: []
        } as any));
        // 讀取發票清單（顯示在近期發票區塊）
        // 暫不帶 companyId，先確保基本列表可用
        const respInv: any = await apiService.getInvoices({ page: 1, limit: 10 });
        const rowsInv: any[] = Array.isArray(respInv?.invoices) ? respInv.invoices : (Array.isArray(respInv) ? respInv : []);
        const mappedInv = (rowsInv || []).map((r: any) => ({
          id: String(r.id),
          invoiceNumber: r.invoiceNo || r.invoiceNumber,
          customer: r.customer?.name || '',
          amount: Number(r.totalAmount || 0),
          status: String(r.status || 'draft').toLowerCase(),
          dueDate: r.dueDate ? new Date(r.dueDate).toISOString().slice(0,10) : ''
        }));
        setSalesData(prev => ({ ...prev, invoices: mappedInv } as any));
      } catch {
        setSalesData(prev => ({ ...prev, invoices: [] } as any));
      }
    };
    loadOverview();
  }, []);

  // 從本地標記返回列表並刷新
  useEffect(() => {
    const wantedTab = localStorage.getItem('sales.activeTab');
    const needRefresh = localStorage.getItem('sales.refreshInvoices') === '1';
    if (wantedTab) {
      setActiveTab(wantedTab);
      localStorage.removeItem('sales.activeTab');
    }
    if (needRefresh && (wantedTab === 'invoices' || activeTab === 'invoices')) {
      (async () => {
        try {
          const respInv: any = await apiService.getInvoices({ page: 1, limit: 10 });
          const rowsInv: any[] = Array.isArray(respInv?.invoices) ? respInv.invoices : (Array.isArray(respInv) ? respInv : []);
          const mappedInv = (rowsInv || []).map((r: any) => ({
            id: String(r.id),
            invoiceNumber: r.invoiceNo || r.invoiceNumber,
            customer: r.customer?.name || '',
            amount: Number(r.totalAmount || 0),
            status: String(r.status || 'draft').toLowerCase(),
            dueDate: r.dueDate ? new Date(r.dueDate).toISOString().slice(0,10) : ''
          }));
          setSalesData(prev => ({ ...prev, invoices: mappedInv } as any));
        } catch {}
        localStorage.removeItem('sales.refreshInvoices');
      })();
    }
  }, [activeTab]);

  // Load quotations from backend with server pagination
  useEffect(() => {
    const loadQuotations = async () => {
      if (activeTab !== 'quotations') return;
      setQuotationLoading(true);
      try {
        const auth = localStorage.getItem('auth-storage');
        const parsed = (() => { try { return auth ? JSON.parse(auth) : null; } catch { return null; } })();
        const companyId = parsed?.state?.user?.companyId || localStorage.getItem('current-company-id') || localStorage.getItem('companyId') || undefined;
        const resp: any = await apiService.getQuotationsV2({ 
          page: quotationPage, 
          limit: quotationPageSize, 
          companyId: companyId || undefined, 
          status: quotationStatus ? quotationStatus.toUpperCase() : undefined,
          search: quotationSearch || undefined,
          dateFrom: quotationDateRange ? quotationDateRange[0] : undefined,
          dateTo: quotationDateRange ? quotationDateRange[1] : undefined
        });
        let rows: any[] = Array.isArray(resp?.quotations) ? resp.quotations : (Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []));
        // fallback: if companyId 過濾導致為空，嘗試不帶 companyId 再查一次
        if ((rows?.length || 0) === 0 && companyId) {
          const respNoCompany: any = await apiService.getQuotationsV2({
            page: quotationPage,
            limit: quotationPageSize,
            status: quotationStatus ? quotationStatus.toUpperCase() : undefined,
            search: quotationSearch || undefined,
            dateFrom: quotationDateRange ? quotationDateRange[0] : undefined,
            dateTo: quotationDateRange ? quotationDateRange[1] : undefined
          });
          rows = Array.isArray(respNoCompany?.quotations) ? respNoCompany.quotations : (Array.isArray(respNoCompany?.data) ? respNoCompany.data : (Array.isArray(respNoCompany) ? respNoCompany : []));
        }

        const mapped = (rows || []).map((q: any) => ({
          id: q.id,
          quoteNumber: q.quotationNo || q.quoteNo || q.quoteNumber || '',
          customer: q.customer?.name || q.customerName || '',
          amount: Number(q.totalAmount || 0),
          status: String(q.status || 'draft').toLowerCase(),
          validUntil: q.validUntil ? new Date(q.validUntil).toISOString().slice(0, 10) : '',
          createdDate: q.createdAt ? new Date(q.createdAt).toISOString().slice(0, 10) : ''
        }));
        setQuotationRows(mapped);
        const p = resp?.pagination || resp?.page ? { total: resp.total, page: resp.page, limit: resp.limit } : undefined;
        setQuotationTotal(Number(p?.total ?? mapped.length));
      } catch (e) {
        setQuotationRows([]);
      } finally {
        setQuotationLoading(false);
      }
    };
    loadQuotations();
  }, [activeTab, quotationPage, quotationPageSize, quotationStatus, quotationSearch, quotationDateRange]);

  const getStatusColor = (status: string) => {
    const colors = {
      draft: 'default',
      sent: 'blue',
      paid: 'green',
      overdue: 'red',
      completed: 'green',
      processing: 'blue',
      pending: 'orange',
      accepted: 'green',
      rejected: 'red',
      expired: 'volcano'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const invoiceColumns: ColumnsType<any> = [
    {
      title: '發票號碼',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: '客戶',
      dataIndex: 'customer',
      key: 'customer'
    },
    {
      title: '金額',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => <Text strong>${amount.toLocaleString()}</Text>
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>{
          ({ draft: '草稿', sent: '已發送', paid: '已付款', overdue: '逾期' } as any)[status] || status
        }</Tag>
      )
    },
    {
      title: '到期日',
      dataIndex: 'dueDate',
      key: 'dueDate'
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} size="small" onClick={async ()=>{
            try {
              const data:any = await apiService.getInvoiceById(record.id);
              Modal.info({ title: `發票 ${data.invoiceNo || record.invoiceNumber}`, width: 720, content: (
                <div>
                  <p><b>客戶:</b> {data?.customer?.name || ''}</p>
                  <p><b>總計:</b> ${Number(data?.totalAmount || 0).toFixed(2)}</p>
                  <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                    <Table size='small' dataSource={(data?.items||[]).map((it:any)=>({ key: it.id, ...it }))} pagination={false}
                      columns={[{title:'產品',dataIndex:['product','itemName'],key:'p'},{title:'數量',dataIndex:'quantity',key:'q'},{title:'單價',dataIndex:'unitPrice',key:'u'},{title:'小計',dataIndex:'lineTotal',key:'l'}]} />
                  </div>
                </div>
              )});
            } catch(e){ message.error('Failed to load invoice'); }
          }}>
            查看
          </Button>
          <Button type="text" icon={<EditOutlined />} size="small" onClick={async ()=>{
            try {
              localStorage.setItem('invoice.editId', String(record.id));
              setEditingInvoiceId(String(record.id));
              setInvoiceModalVisible(true);
            } catch(e){ message.error('Failed to load invoice'); }
          }}>
            編輯
          </Button>
          <Button type="text" icon={<SendOutlined />} size="small" onClick={async ()=>{
            try { await apiService.updateInvoiceStatus?.(record.id,'SENT'); message.success('已發送'); }
            catch(e){ message.error('發送失敗'); }
          }}>
            發送
          </Button>
          <Popconfirm
            title="確定刪除此發票？"
            okText="刪除"
            okType="danger"
            onConfirm={async ()=>{
              try {
                await apiService.deleteInvoice(record.id);
                setSalesData(prev => ({
                  ...prev,
                  invoices: (prev.invoices || []).filter((inv: any) => inv.id !== record.id)
                } as any));
                message.success('已刪除發票');
              } catch(e) { message.error('刪除失敗'); }
            }}
          >
            <Button type="text" danger icon={<DeleteOutlined />} size="small">
              刪除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const quotationColumns: ColumnsType<any> = [
    {
      title: '報價單號',
      dataIndex: 'quoteNumber',
      key: 'quoteNumber',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: '客戶',
      dataIndex: 'customer',
      key: 'customer'
    },
    {
      title: '金額',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => <Text strong>${amount.toLocaleString()}</Text>
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>{
          ({ draft: '草稿', sent: '已發送', accepted: '已接受', rejected: '已拒絕', expired: '已過期' } as any)[status] || status
        }</Tag>
      )
    },
    {
      title: '有效期限',
      dataIndex: 'validUntil',
      key: 'validUntil'
    },
    {
      title: '建立日期',
      dataIndex: 'createdDate',
      key: 'createdDate'
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} size="small" onClick={async () => {
            try {
              const data: any = await apiService.getQuotationByIdV2(record.id);
              Modal.info({ title: `報價單 ${data.quotationNo || record.quoteNumber}`, width: 720, content: (
                <div>
                  <p><b>客戶:</b> {data?.customer?.name || ''}</p>
                  <p><b>總計:</b> ${Number(data?.totalAmount || 0).toFixed(2)}</p>
                  <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                    <Table size='small' dataSource={(data?.items||[]).map((it:any)=>({ key: it.id, ...it }))} pagination={false}
                      columns={[{title:'產品',dataIndex:['product','itemName'],key:'p'},{title:'數量',dataIndex:'quantity',key:'q'},{title:'單價',dataIndex:'unitPrice',key:'u'},{title:'小計',dataIndex:'lineTotal',key:'l'}]} />
                  </div>
                </div>
              )});
            } catch(e){ message.error('Failed to load quotation details'); }
          }}>
            查看
          </Button>
          <Button type="text" icon={<EditOutlined />} size="small" onClick={() => {
            localStorage.setItem('quotation.editId', String(record.id));
            setEditingQuotationId(String(record.id));
            setQuotationModalVisible(true);
          }}>
            編輯
          </Button>
          <Button type="text" icon={<SendOutlined />} size="small" onClick={async ()=>{
            try { await apiService.updateQuotationStatusV2(record.id,'SENT'); message.success('已發送'); setQuotationPage(1); }
            catch(e){ message.error('發送失敗'); }
          }}>
            發送
          </Button>
          <Button type="text" icon={<CopyOutlined />} size="small" onClick={async ()=>{
            try {
              const data:any = await apiService.getQuotationByIdV2(record.id);
              const auth = localStorage.getItem('auth-storage');
              const parsed = (() => { try { return auth ? JSON.parse(auth) : null; } catch { return null; } })();
              const userId = data.userId || parsed?.state?.user?.id || localStorage.getItem('current-user-id');
              const companyId = data.companyId || parsed?.state?.user?.companyId || localStorage.getItem('current-company-id');
              const items = (data.items||[]).map((it:any)=>({ productId: it.productId, quantity: it.quantity, unitPrice: Number(it.unitPrice||0) }));
              if (!userId || !companyId) { message.error('Missing user/company context for duplicate'); return; }
              if (!items.length) { message.error('Source quotation has no items'); return; }
              const payload = { customerId: data.customerId, userId, companyId, validUntil: (data.validUntil ? new Date(data.validUntil).toISOString() : new Date(Date.now()+30*24*60*60*1000).toISOString()), items };
              await apiService.createQuotation(payload);
              message.success('已複製');
              setQuotationPage(1);
            } catch(e){ message.error('Duplicate failed'); }
          }}>
            複製
          </Button>
          <Popconfirm title='確定刪除此報價單？' onConfirm={async ()=>{
            try { await apiService.deleteQuotationV2(record.id); message.success('已刪除'); setQuotationRows(prev=>prev.filter((r:any)=>r.id!==record.id)); setQuotationTotal(t=>t>0?t-1:0); }
            catch(e){ message.error('刪除失敗'); }
          }}>
            <Button type="text" danger size="small" icon={<DeleteOutlined />}>
              刪除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const orderColumns: ColumnsType<any> = [
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer'
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => <Text strong>${amount.toLocaleString()}</Text>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
      )
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} size="small" />
          <Button type="link" icon={<EditOutlined />} size="small" />
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: isMobile ? '12px' : '20px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <Card style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
            <div style={{ minWidth: 0 }}>
              <Title level={isMobile ? 3 : 2} style={{ margin: 0 }}>
                <BarChartOutlined style={{ marginRight: 8 }} />
                銷售總覽
              </Title>
              <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                完整的銷售管理與分析儀表板
              </Text>
            </div>
            {/*<Space wrap size={isMobile ? 4 : 8} style={{ justifyContent: 'flex-end' }}>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={openCreateInvoice}
                size={isMobile ? 'small' : 'middle'}
                {...getMobileButtonProps(isMobile)}
              >
                {isMobile ? '' : 'Create Invoice'}
              </Button>
              <Button 
                icon={<PlusOutlined />}
                onClick={openCreateQuotation}
                size={isMobile ? 'small' : 'middle'}
                {...getMobileButtonProps(isMobile)}
              >
                {isMobile ? '' : 'Create Quotation'}
              </Button>
              <Button 
                icon={<PlusOutlined />}
                onClick={() => setOrderBuilderVisible(true)}
                size={isMobile ? 'small' : 'middle'}
                {...getMobileButtonProps(isMobile)}
              >
                {isMobile ? '' : 'Create Sales Order'}
              </Button>
              <Button 
                icon={<FileTextOutlined />}
                size={isMobile ? 'small' : 'middle'}
                {...getMobileButtonProps(isMobile)}
              >
                {isMobile ? '' : 'Generate Report'}
              </Button>
              <Button 
                icon={<CalendarOutlined />}
                size={isMobile ? 'small' : 'middle'}
                {...getMobileButtonProps(isMobile)}
              >
                {isMobile ? '' : 'Schedule Follow-up'}
              </Button>
            </Space>*/}
          </div>
        </Card>

        {/* Main Content Tabs */}
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="概覽" key="overview">
          {/* Statistics Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="總銷售額"
                  value={salesData.totalRevenue}
                  prefix={<DollarOutlined />}
                  precision={0}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="訂單總數"
                  value={salesData.totalOrders}
                  prefix={<ShoppingCartOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="平均訂單金額"
                  value={salesData.avgOrderValue}
                  prefix={<DollarOutlined />}
                  precision={0}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="轉換率"
                  value={salesData.conversionRate}
                  prefix={<TrophyOutlined />}
                  suffix="%"
                  precision={1}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
            {/* Top Products */}
            <Col xs={24} lg={12}>
              <Card title="熱銷產品" extra={<Button type="link">查看全部</Button>}>
                <List
                  dataSource={salesData.topProducts}
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<Avatar icon={<ShoppingCartOutlined />} />}
                        title={item.name}
                        description={`${item.sales} 筆銷售 • $${item.revenue.toLocaleString()}`}
                      />
                      <div>
                        <Progress 
                          percent={(item.revenue / 100000) * 100} 
                          size="small" 
                          showInfo={false}
                        />
                      </div>
                    </List.Item>
                  )}
                />
              </Card>
            </Col>

            {/* Recent Activity */}
            <Col xs={24} lg={12}>
              <Card title="最近訂單" extra={<Button type="link">查看全部</Button>}>
                <Table
                  dataSource={salesData.recentOrders}
                  columns={orderColumns}
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="報價單" key="quotations">
          <Card 
            title="報價管理" 
            extra={
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={openCreateQuotation}
              >
                新增報價
              </Button>
            }
          >
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Search
                  placeholder="搜尋報價單..."
                  style={{ width: 300 }}
                  allowClear
                  onSearch={(v) => setQuotationSearch(v.trim())}
                  onChange={(e) => setQuotationSearch(e.target.value.trim())}
                />
                <Select 
                  placeholder="依狀態篩選" 
                  style={{ width: 150 }}
                  allowClear
                  value={quotationStatus}
                  onChange={(v) => setQuotationStatus(v || undefined)}
                >
                  <Select.Option value="draft">草稿</Select.Option>
                  <Select.Option value="sent">已發送</Select.Option>
                  <Select.Option value="accepted">已接受</Select.Option>
                  <Select.Option value="rejected">已拒絕</Select.Option>
                  <Select.Option value="expired">已過期</Select.Option>
                </Select>
                <RangePicker 
                  placeholder={["開始日期", "結束日期"]}
                  onChange={(dates) => {
                    if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) {
                      setQuotationDateRange(null);
                    } else {
                      setQuotationDateRange([
                        dates[0].startOf('day').format('YYYY-MM-DD'),
                        dates[1].endOf('day').format('YYYY-MM-DD')
                      ] as [string, string]);
                    }
                  }}
                />
              </Space>
            </div>
            <Table
              dataSource={quotationRows}
              columns={quotationColumns}
              loading={quotationLoading}
              rowKey="id"
              pagination={{
                current: quotationPage,
                pageSize: quotationPageSize,
                total: quotationTotal,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: [10, 20, 50, 100],
                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 條，共 ${total} 條`,
                onChange: (page, pageSize) => {
                  setQuotationPage(page);
                  setQuotationPageSize(pageSize);
                }
              }}
            />
          </Card>
        </TabPane>

        {/* Remove Create Quotation tab – use modal instead */}

        <TabPane tab="訂單" key="orders">
          <SalesOrderList />
        </TabPane>

        {/* Remove Create Order tab – use modal instead */}

        <TabPane tab="發票" key="invoices">
          <Card 
            title="發票管理" 
            extra={
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={openCreateInvoice}
              >
                新增發票
              </Button>
            }
          >
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Search
                  placeholder="搜尋發票..."
                  style={{ width: 300 }}
                />
                <Select placeholder="依狀態篩選" style={{ width: 150 }}>
                  <Select.Option value="all">全部狀態</Select.Option>
                  <Select.Option value="draft">草稿</Select.Option>
                  <Select.Option value="sent">已發送</Select.Option>
                  <Select.Option value="paid">已付款</Select.Option>
                  <Select.Option value="overdue">逾期</Select.Option>
                </Select>
                <RangePicker placeholder={['開始日期', '結束日期']} />
              </Space>
            </div>
            <Table
              dataSource={salesData.invoices}
              columns={invoiceColumns}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true
              }}
            />
          </Card>
        </TabPane>

        {/* 移除 Create Invoice 獨立分頁，改用彈窗 */}

        <TabPane tab="分析" key="analytics">
          {/* Analytics Header */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="月度銷售額"
                  value={salesData.totalRevenue}
                  precision={0}
                  valueStyle={{ color: '#3f8600' }}
                  prefix={<DollarOutlined />}
                  suffix="USD"
                />
                <div style={{ marginTop: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    <span style={{ color: '#3f8600' }}>↗ +12.5%</span> 較上月
                  </Text>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="訂單數"
                  value={salesData.totalOrders}
                  valueStyle={{ color: '#1890ff' }}
                  prefix={<ShoppingCartOutlined />}
                />
                <div style={{ marginTop: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    <span style={{ color: '#3f8600' }}>↗ +8.2%</span> 較上月
                  </Text>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="轉換率"
                  value={salesData.conversionRate}
                  precision={1}
                  valueStyle={{ color: '#722ed1' }}
                  prefix={<TrophyOutlined />}
                  suffix="%"
                />
                <div style={{ marginTop: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    <span style={{ color: '#cf1322' }}>↘ -2.1%</span> 較上月
                  </Text>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="客戶滿意度"
                  value={94.3}
                  precision={1}
                  valueStyle={{ color: '#fa8c16' }}
                  prefix={<UserOutlined />}
                  suffix="%"
                />
                <div style={{ marginTop: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    <span style={{ color: '#3f8600' }}>↗ +1.8%</span> 較上月
                  </Text>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Charts Section */}
          <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
            {/* Sales Trend Chart */}
            <Col xs={24} lg={16}>
              <SalesTrendChart />
            </Col>

            {/* Top Products */}
            <Col xs={24} lg={8}>
              <Card title="熱銷產品" extra={<Button type="link" size="small">查看全部</Button>}>
                <List
                  dataSource={salesData.topProducts}
                  renderItem={(item, index) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <Badge count={index + 1} style={{ backgroundColor: index < 3 ? '#faad14' : '#d9d9d9' }}>
                            <Avatar icon={<ShoppingCartOutlined />} />
                          </Badge>
                        }
                        title={
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text strong>{item.name}</Text>
                            <Text type="secondary">${item.revenue.toLocaleString()}</Text>
                          </div>
                        }
                        description={
                          <div>
                            <div>{item.sales} sales</div>
                            <Progress 
                              percent={Math.round((item.revenue / salesData.topProducts[0].revenue) * 100)} 
                              size="small" 
                              showInfo={false}
                              strokeColor="#1890ff"
                            />
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
          </Row>

          {/* Detailed Analytics */}
          <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
            {/* Revenue Breakdown */}
            <Col xs={24} lg={12}>
              <RevenueBreakdownChart totalRevenue={salesData.totalRevenue} />
            </Col>

            {/* Customer Analytics */}
            <Col xs={24} lg={12}>
              <Card title="客戶分析">
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <div style={{ textAlign: 'center', padding: '16px' }}>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#722ed1' }}>
                        1,245
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>客戶總數</div>
                      <div style={{ fontSize: '10px', color: '#3f8600' }}>↗ +120 新增</div>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ textAlign: 'center', padding: '16px' }}>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#fa8c16' }}>
                        890
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>活躍客戶</div>
                      <div style={{ fontSize: '10px', color: '#3f8600' }}>↗ +45</div>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ textAlign: 'center', padding: '16px' }}>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#13c2c2' }}>
                        2.3
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>平均訂單數</div>
                      <div style={{ fontSize: '10px', color: '#cf1322' }}>↘ -0.2</div>
                    </div>
                  </Col>
                  <Col span={24}>
                    <Divider style={{ margin: '16px 0' }} />
                    <Timeline size="small">
                      <Timeline.Item color="green" dot={<UserOutlined />}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div>
                            <Text strong>新客戶註冊</Text>
                            <div style={{ fontSize: '12px', color: '#666' }}>Acme Corporation 已加入</div>
                          </div>
                          <Text type="secondary" style={{ fontSize: '12px' }}>2 hours ago</Text>
                        </div>
                      </Timeline.Item>
                      <Timeline.Item color="blue" dot={<ShoppingCartOutlined />}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div>
                            <Text strong>大型訂單完成</Text>
                            <div style={{ fontSize: '12px', color: '#666' }}>$12,500 訂單（Global Tech）</div>
                          </div>
                          <Text type="secondary" style={{ fontSize: '12px' }}>5 hours ago</Text>
                        </div>
                      </Timeline.Item>
                      <Timeline.Item color="orange" dot={<TrophyOutlined />}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div>
                            <Text strong>達成月度目標</Text>
                            <div style={{ fontSize: '12px', color: '#666' }}>達成 105% 銷售目標</div>
                          </div>
                          <Text type="secondary" style={{ fontSize: '12px' }}>1 day ago</Text>
                        </div>
                      </Timeline.Item>
                    </Timeline>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          {/* Performance Metrics */}
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Card title="績效指標" extra={
                <Space>
                  <RangePicker />
                  <Button icon={<FileTextOutlined />}>匯出報表</Button>
                </Space>
              }>
                <Row gutter={[24, 24]}>
                  <Col xs={24} sm={12} md={6}>
                    <div style={{ textAlign: 'center', padding: '20px', border: '1px solid #f0f0f0', borderRadius: '6px' }}>
                      <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1890ff', marginBottom: '8px' }}>
                        ${salesData.avgOrderValue.toLocaleString()}
                      </div>
                      <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>平均訂單金額</div>
                      <div style={{ fontSize: '12px', color: '#3f8600' }}>↗ +5.2% 較前期</div>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <div style={{ textAlign: 'center', padding: '20px', border: '1px solid #f0f0f0', borderRadius: '6px' }}>
                      <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#52c41a', marginBottom: '8px' }}>
                        {salesData.conversionRate}%
                      </div>
                      <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>轉換率</div>
                      <div style={{ fontSize: '12px', color: '#cf1322' }}>↘ -1.3% 較前期</div>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <div style={{ textAlign: 'center', padding: '20px', border: '1px solid #f0f0f0', borderRadius: '6px' }}>
                      <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#722ed1', marginBottom: '8px' }}>
                        24.7
                      </div>
                      <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>應收帳齡（天）</div>
                      <div style={{ fontSize: '12px', color: '#3f8600' }}>↗ +2.1 天改善</div>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <div style={{ textAlign: 'center', padding: '20px', border: '1px solid #f0f0f0', borderRadius: '6px' }}>
                      <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#fa8c16', marginBottom: '8px' }}>
                        92.4%
                      </div>
                      <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>客戶留存率</div>
                      <div style={{ fontSize: '12px', color: '#3f8600' }}>↗ +3.7% 較前期</div>
                    </div>
                  </Col>
                </Row>
                
                <Divider />
                
                {/* Quick Actions */}
                <div style={{ textAlign: 'center' }}>
                  <Space size="large">
                    <Button type="primary" icon={<FileTextOutlined />} size="large">
                      產生完整報表
                    </Button>
                    <Button icon={<CalendarOutlined />} size="large">
                      排程報表
                    </Button>
                    <Button icon={<TeamOutlined />} size="large">
                      分享分析
                    </Button>
                  </Space>
                </div>
              </Card>
            </Col>
          </Row>
        </TabPane>
      </Tabs>
      {/* Create/Edit Quotation Modal */}
      <Modal
        open={quotationModalVisible}
        onCancel={() => setQuotationModalVisible(false)}
        footer={null}
        width={1200}
        destroyOnClose
      >
        <QuotationBuilder
          quotationId={editingQuotationId || undefined}
          onClose={() => setQuotationModalVisible(false)}
          onSave={async () => {
            setQuotationModalVisible(false);
            // 重新刷新 Quotation 列表
            try {
              const auth = localStorage.getItem('auth-storage');
              const parsed = (() => { try { return auth ? JSON.parse(auth) : null; } catch { return null; } })();
              const companyId = parsed?.state?.user?.companyId || localStorage.getItem('current-company-id') || localStorage.getItem('companyId') || undefined;
              const resp: any = await apiService.getQuotationsV2({ page: quotationPage, limit: quotationPageSize, companyId: companyId || undefined, status: quotationStatus ? quotationStatus.toUpperCase() : undefined, search: quotationSearch || undefined, dateFrom: quotationDateRange ? quotationDateRange[0] : undefined, dateTo: quotationDateRange ? quotationDateRange[1] : undefined });
              let rows: any[] = Array.isArray(resp?.quotations) ? resp.quotations : (Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []));
              const mapped = (rows || []).map((q: any) => ({
                id: q.id,
                quoteNumber: q.quotationNo || q.quoteNo || q.quoteNumber || '',
                customer: q.customer?.name || q.customerName || '',
                amount: Number(q.totalAmount || 0),
                status: String(q.status || 'draft').toLowerCase(),
                validUntil: q.validUntil ? new Date(q.validUntil).toISOString().slice(0, 10) : '',
                createdDate: q.createdAt ? new Date(q.createdAt).toISOString().slice(0, 10) : ''
              }));
              setQuotationRows(mapped);
              const p = resp?.pagination || resp?.page ? { total: resp.total, page: resp.page, limit: resp.limit } : undefined;
              setQuotationTotal(Number(p?.total ?? mapped.length));
              message.success('Quotation saved');
            } catch { /* ignore */ }
          }}
        />
      </Modal>

      {/* Create/Edit Sales Order Modal */}
      <Modal
        open={orderBuilderVisible}
        onCancel={() => setOrderBuilderVisible(false)}
        footer={null}
        width={1200}
        destroyOnClose
      >
        <SalesOrderBuilder onCancel={() => setOrderBuilderVisible(false)} />
      </Modal>
      {/* Create/Edit Invoice Modal */}
      <Modal
        open={invoiceModalVisible}
        onCancel={() => setInvoiceModalVisible(false)}
        footer={null}
        width={1200}
        destroyOnClose
      >
        <InvoiceBuilder 
          onCancel={() => setInvoiceModalVisible(false)}
          editId={editingInvoiceId || undefined}
          onSaved={() => {
            setInvoiceModalVisible(false);
            // 重新拉取列表數據
            (async () => {
              try {
                const respInv: any = await apiService.getInvoices({ page: 1, limit: 10 });
                const rowsInv: any[] = Array.isArray(respInv?.invoices) ? respInv.invoices : (Array.isArray(respInv) ? respInv : []);
                const mappedInv = (rowsInv || []).map((r: any) => ({
                  id: String(r.id),
                  invoiceNumber: r.invoiceNo || r.invoiceNumber,
                  customer: r.customer?.name || '',
                  amount: Number(r.totalAmount || 0),
                  status: String(r.status || 'draft').toLowerCase(),
                  dueDate: r.dueDate ? new Date(r.dueDate).toISOString().slice(0,10) : ''
                }));
                setSalesData(prev => ({ ...prev, invoices: mappedInv } as any));
                message.success('Invoice saved');
              } catch { /* ignore */ }
            })();
          }}
        />
      </Modal>
      </div>
    </div>
  );
};

export default SalesOverview;
export { SalesOverview }; 